import { ModuleRegistry } from "@ag-grid-community/core";
import { ExcelExportModule } from "@ag-grid-enterprise/excel-export";
import {
  Box,
  Button,
  Grid,
  MenuItem,
  Select,
  Typography,
  useMediaQuery,
} from "@mui/material";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CustomTableMobile from "./CustomTableMobile";

ModuleRegistry.registerModules([ExcelExportModule]);

const localeText = {
  page: "صفحه",
  to: "تا",
  of: "از",
  next: "بعدی",
  previous: "قبلی",
  last: "آخرین",
  first: "اولین",
  loadingOoo: "در حال بارگذاری...",
  noRowsToShow: "داده‌ای برای نمایش وجود ندارد",
  pageSizeSelectorLabel: "تعداد سطر در هر صفحه", // ترجمه Page Size
  rowGroupColumnsEmptyMessage: "برای گروه‌بندی، ستون را اینجا بکشید",
  // سایر متون مورد نیاز...
};

export default function CustomTable({
  rowData,
  totalElements,
  onPageChange,
  pageSize,
  currentPage,
  columns,
  selection,
  setSelection,
  onCellClicked,
  exportExcel,
  clearSelection,
  refetching = false,
  needPagination = true,
  completePagination = true,
  hasMobileActionBtn = false,
  customHeight = undefined,
  offlinePagination = false,
  allowChangePageSize = true,
  rowHeight = "42px",
}) {
  // دو ref جداگانه برای کامپوننت و API
  const gridRef = useRef(); // برای کامپوننت
  const gridApiRef = useRef(); // برای API

  const [gridReady, setGridReady] = useState(false);

  const onBtExport = useCallback(() => {
    gridRef.current.api.exportDataAsExcel();
  }, []);

  useEffect(() => {
    if (exportExcel === true) onBtExport();
  }, [exportExcel]);

  const onGridReady = useCallback((params) => {
    gridApiRef.current = params.api; // ذخیره API در ref جداگانه
    setGridReady(true);
  }, []);

  const KEYBOARD_STATE = useRef({
    isShiftPressed: false,
  });

  useEffect(() => {
    const handleKeyDown = (e) => {
      KEYBOARD_STATE.current.isShiftPressed = e.key === "Shift";
    };
    const handleKeyUp = (e) => {
      KEYBOARD_STATE.current.isShiftPressed = false;
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("keyup", handleKeyUp);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("keyup", handleKeyUp);
    };
  }, []);

  useEffect(() => {
    if (clearSelection === true) {
      gridRef.current.api.deselectAll();
    }
  }, [clearSelection]);

  const onSelectionChanged = (e) => {
    const selectedNodes = e.api.getSelectedNodes();
    const lastSelectedNode =
      selectedNodes[0]?.selectionController?.lastSelectedNode;
    const selectedNodeFrom = lastSelectedNode || e.api.getRenderedNodes()[0];
    const isRangeSelect = KEYBOARD_STATE.current.isShiftPressed;

    const difference = (arr1, arr2) => arr1.filter((x) => !arr2.includes(x));
    const newSelection = difference(selectedNodes, selection);

    if (newSelection.length > 0) {
      if (isRangeSelect) {
        const isSelectBackward =
          newSelection[0].rowIndex < selectedNodeFrom.rowIndex;

        if (isSelectBackward) {
          newSelection.reverse();
        }
      }

      newSelection.forEach((n) => updateSelection(n));
    } else {
      updateSelection();
    }
  };

  const updateSelection = (rowNode) => {
    setSelection((selections) => {
      if (rowNode) {
        const isSelected = rowNode.isSelected();

        if (isSelected) {
          return [...selections, rowNode];
        } else {
          return selections.filter((s) => s.id !== rowNode.id);
        }
      } else {
        return selections.filter((n) => n.isSelected());
      }
    });
  };

  const isPageChangeInProgress = useRef(false);
  const lastProcessedPage = useRef(currentPage);
  const lastProcessedPageSize = useRef(pageSize);
  const [localPageSize, setLocalPageSize] = useState(pageSize);
  const [localCurrentPage, setLocalCurrentPage] = useState(currentPage);

  const totalPages = useMemo(() => {
    if (!totalElements) return 0;
    return Math.max(1, Math.ceil(totalElements / localPageSize));
  }, [totalElements, localPageSize]);

  const goToPage = (page) => {
    if (page >= 0 && page < totalPages && !isPageChangeInProgress.current) {
      isPageChangeInProgress.current = true;
      setLocalCurrentPage(page);

      if (onPageChange) {
        onPageChange(page, localPageSize);
      }

      // استفاده از gridApiRef به جای gridRef
      if (gridApiRef.current) {
        gridApiRef.current.paginationGoToPage(page);
      }

      setTimeout(() => {
        isPageChangeInProgress.current = false;
        lastProcessedPage.current = page;
      }, 100);
    }
  };

  const goToNextPage = () => {
    goToPage(localCurrentPage + 1);
  };

  const goToPreviousPage = () => {
    goToPage(localCurrentPage - 1);
  };

  const goToFirstPage = () => {
    goToPage(0);
  };

  const goToLastPage = () => {
    goToPage(totalPages - 1);
  };

  // استفاده از gridApiRef در useEffectها
  useEffect(() => {
    if (gridReady && Array.isArray(rowData) && gridApiRef.current) {
      gridApiRef.current.setGridOption("rowData", rowData);

      setTimeout(() => {
        if (gridApiRef.current) {
          gridApiRef.current.paginationGoToPage(localCurrentPage);
        }
      }, 200);
    }
  }, [rowData, gridReady, localCurrentPage]);

  const updatePaginationInfo = useCallback(() => {
    if (!gridReady) return;

    setTimeout(() => {
      const paginationElement = document.querySelector(
        ".ag-paging-row-summary-panel",
      );
      if (paginationElement) {
        paginationElement.textContent = `صفحه ${
          localCurrentPage + 1
        } از ${totalPages}`;
      }
    }, 100);
  }, [localCurrentPage, totalPages, gridReady]);

  useEffect(() => {
    if (gridReady) {
      updatePaginationInfo();
    }
  }, [totalElements, gridReady, updatePaginationInfo]);

  useEffect(() => {
    if (
      gridReady &&
      gridApiRef.current &&
      (currentPage !== lastProcessedPage.current ||
        pageSize !== lastProcessedPageSize.current)
    ) {
      setLocalCurrentPage(currentPage);
      setLocalPageSize(pageSize);
      lastProcessedPage.current = currentPage;
      lastProcessedPageSize.current = pageSize;

      setTimeout(() => {
        if (gridApiRef.current) {
          gridApiRef.current.paginationSetPageSize(pageSize);
          gridApiRef.current.paginationGoToPage(currentPage);
        }
      }, 50);
    }
  }, [currentPage, pageSize, gridReady]);

  const handlePageSizeChange = (event) => {
    const newSize = parseInt(event.target.value);
    setLocalPageSize(newSize);
    setLocalCurrentPage(0);
    if (onPageChange) {
      onPageChange(0, newSize);
    }
  };

  const CustomPaginationControls = () => (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      padding="10px"
      bgcolor="#f5f5f5"
      borderRadius="8px"
      marginTop="10px"
      flexWrap="wrap"
      gap={2}
    >
      <Box display="flex" alignItems="center" gap={1}>
        <Typography fontFamily="Vazirmatn" variant="body2">
          تعداد رکوردها:
        </Typography>
        <Select
          value={localPageSize}
          onChange={handlePageSizeChange}
          size="small"
          sx={{
            minWidth: 80,
            fontFamily: "Vazirmatn",
            "& .MuiSelect-select": { py: 0.5 },
          }}
        >
          <MenuItem value={10} sx={{ fontFamily: "Vazirmatn" }}>
            ۱۰
          </MenuItem>
          <MenuItem value={20} sx={{ fontFamily: "Vazirmatn" }}>
            ۲۰
          </MenuItem>
          <MenuItem value={50} sx={{ fontFamily: "Vazirmatn" }}>
            ۵۰
          </MenuItem>
          <MenuItem value={100} sx={{ fontFamily: "Vazirmatn" }}>
            ۱۰۰
          </MenuItem>
        </Select>
      </Box>

      <Box display="flex" alignItems="center" gap={1}>
        <Typography fontFamily="Vazirmatn" variant="body2">
          صفحه:
        </Typography>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Button
            variant="outlined"
            size="small"
            onClick={goToFirstPage}
            disabled={localCurrentPage === 0 || refetching}
            sx={{ minWidth: "unset !important" }}
          >
            ⟪
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={goToPreviousPage}
            disabled={localCurrentPage === 0 || refetching}
            sx={{ minWidth: "unset !important" }}
          >
            ⟨
          </Button>
          <Box px={1}>
            <Typography fontFamily="Vazirmatn" variant="body2">
              {localCurrentPage + 1} از {totalPages}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            onClick={goToNextPage}
            disabled={localCurrentPage >= totalPages - 1 || refetching}
            sx={{ minWidth: "unset !important" }}
          >
            ⟩
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={goToLastPage}
            disabled={localCurrentPage >= totalPages - 1 || refetching}
            sx={{ minWidth: "unset !important" }}
          >
            ⟫
          </Button>
        </Box>
      </Box>

      <Box display="flex" alignItems="center" gap={1}>
        <Typography fontFamily="Vazirmatn" variant="body2">
          برو به صفحه:
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <input
            type="number"
            min="1"
            max={totalPages}
            value={localCurrentPage + 1}
            disabled={refetching}
            onChange={(e) => {
              const pageNum = parseInt(e.target.value);
              if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= totalPages) {
                goToPage(pageNum - 1);
              }
            }}
            style={{
              width: "60px",
              padding: "4px 8px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              textAlign: "center",
              fontFamily: "Vazirmatn",
            }}
          />
          <Button
            variant="contained"
            size="small"
            disabled={refetching}
            onClick={() => {
              const input = document.querySelector('input[type="number"]');
              if (input) {
                const pageNum = parseInt(input.value);
                if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= totalPages) {
                  goToPage(pageNum - 1);
                }
              }
            }}
            sx={{
              fontFamily: "Vazirmatn",
              backgroundColor: "var(--emr-blue)",
              "&:hover": { backgroundColor: "var(--emr-blue-dark)" },
            }}
          >
            برو
          </Button>
        </Box>
      </Box>

      <Typography fontFamily="Vazirmatn" variant="body2">
        کل رکوردها: {totalElements?.toLocaleString("fa-IR")}
      </Typography>
    </Box>
  );

  const CustomMiniPaginationControls = () => (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      padding="10px"
      bgcolor="#f5f5f5"
      borderRadius="8px"
      marginTop="20px"
      flexWrap="wrap"
      gap={2}
    >
      {allowChangePageSize && (
        <Box display="flex" alignItems="center" gap={1}>
          <Typography fontFamily="Vazirmatn" variant="caption">
            تعداد رکوردها:
          </Typography>
          <Select
            value={localPageSize}
            onChange={handlePageSizeChange}
            size="small"
            sx={{
              minWidth: 80,
              fontSize: "12px",
              fontFamily: "Vazirmatn",
              "& .MuiSelect-select": { py: 0.5 },
            }}
          >
            <MenuItem value={10} sx={{ fontFamily: "Vazirmatn" }}>
              ۱۰
            </MenuItem>
            <MenuItem value={20} sx={{ fontFamily: "Vazirmatn" }}>
              ۲۰
            </MenuItem>
            <MenuItem value={50} sx={{ fontFamily: "Vazirmatn" }}>
              ۵۰
            </MenuItem>
            <MenuItem value={100} sx={{ fontFamily: "Vazirmatn" }}>
              ۱۰۰
            </MenuItem>
          </Select>
        </Box>
      )}
      {!allowChangePageSize && (
        <Box display="flex" alignItems="center" gap={1}>
          <Typography fontFamily="Vazirmatn" variant="caption">
            {`تعداد رکوردها: ${totalElements}`}
          </Typography>
        </Box>
      )}

      <Box display="flex" alignItems="center" gap={1}>
        <Typography fontFamily="Vazirmatn" variant="caption">
          صفحه:
        </Typography>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Button
            variant="outlined"
            size="small"
            onClick={goToPreviousPage}
            disabled={localCurrentPage === 0}
            sx={{ minWidth: "unset !important" }}
          >
            ⟨
          </Button>
          <Box px={1}>
            <Typography fontFamily="Vazirmatn" variant="caption">
              {localCurrentPage + 1} از {totalPages}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            onClick={goToNextPage}
            disabled={localCurrentPage >= totalPages - 1}
            sx={{ minWidth: "unset !important" }}
          >
            ⟩
          </Button>
        </Box>
      </Box>
    </Box>
  );

  const defaultColDef = useMemo(() => ({
    filter: true,
    editable: true,
  }));

  const md = useMediaQuery("(max-width:450px)");
  if (md) {
    return (
      <Grid mb={hasMobileActionBtn ? "80px" : undefined}>
        <CustomTableMobile
          columns={columns}
          rowData={rowData}
          actions={[
            ...(onCellClicked
              ? [
                  {
                    label: "مشاهده",
                    color: "primary",
                    onClick: (row) => {
                      onCellClicked?.({
                        data: row,
                        colDef: { field: "__mobile_action_view" },
                      });
                    },
                  },
                ]
              : []),
          ]}
        />

        {needPagination && <CustomMiniPaginationControls />}
      </Grid>
    );
  }

  return (
    <>
      <Grid
        className="ag-theme-quartz"
        style={{
          // display: "contents",
          height: customHeight ? customHeight : "unset", //"calc(100vh - 300px)"
          marginTop: 1,
        }}
      >
        {/* {rowData?.length === 0 && (
          <Typography
            color={"text.primary"}
            variant="h6"
            py={"20px"}
            textAlign={"center"}
            fontFamily={"Vazirmatn"}
          >
            داده ای موجود نیست
          </Typography>
        )} */}
        <AgGridReact
          pagination={offlinePagination}
          paginationPageSize={5}
          paginationPageSizeSelector={false}
          domLayout="autoHeight"
          localeText={localeText}
          enableRtl={true}
          rowHeight={rowHeight}
          ref={gridRef}
          onGridReady={onGridReady}
          columnDefs={columns}
          defaultColDef={defaultColDef}
          rowData={rowData ?? []}
          onSelectionChanged={onSelectionChanged}
          rowSelection="multiple"
          suppressRowClickSelection={true}
          onCellClicked={onCellClicked}
          exportExcel={exportExcel}
        />
        {gridReady && needPagination && completePagination === true && (
          <CustomPaginationControls />
        )}
        {gridReady && needPagination && completePagination === false && (
          <CustomMiniPaginationControls />
        )}
      </Grid>
    </>
  );
}
